import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { api, getSessionCookieToken } from '../api/client'
import { IntegrationLogo } from './IntegrationLogo'

type GitHubStatus = {
  installed: boolean
  installation_count: number
  repo_count: number
  error_code: string | null
}

export function GitHubAppCard({ onStatusChange }: { onStatusChange?: () => void }) {
  const { getToken } = useAuth()
  const [status, setStatus] = useState<GitHubStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)

  // Read URL params after GitHub redirects back to this page.
  const searchParams = new URLSearchParams(window.location.search)
  const setupAction = searchParams.get('setup_action')
  const errorCode = searchParams.get('error_code')

  const authToken = useCallback(async () => {
    const token = getSessionCookieToken() ?? (await getToken())
    if (!token) throw new Error('Not authenticated')
    return token
  }, [getToken])

  const fetchStatus = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await authToken()
      const s = await api.getGithubStatus(token)
      setStatus(s)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [authToken])

  useEffect(() => {
    void fetchStatus()
    // If GitHub just redirected back, open expanded to show result.
    if (setupAction || errorCode) setExpanded(true)
  }, [fetchStatus, setupAction, errorCode])

  async function handleInstall() {
    setConnecting(true)
    setError(null)
    try {
      const token = await authToken()
      const redirectUri = window.location.origin + window.location.pathname
      const { install_url } = await api.getGithubInstallUrl(token, redirectUri)
      window.location.href = install_url
    } catch (err) {
      setError((err as Error).message)
      setConnecting(false)
    }
  }

  async function handleDisconnect() {
    if (!window.confirm('Disconnect GitHub App? This removes all linked installations.')) return
    setDisconnecting(true)
    setError(null)
    try {
      const token = await authToken()
      await api.disconnectGithub(token)
      await fetchStatus()
      onStatusChange?.()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setDisconnecting(false)
    }
  }

  const isConnected = status?.installed === true

  return (
    <div className={`cred-card ${expanded ? 'cred-card--open' : ''}`} style={{ marginBottom: '8px' }}>
      <button
        type="button"
        className="cred-card-header"
        onClick={() => setExpanded((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
      >
        <span className="cred-card-icon" style={{ display: 'flex', alignItems: 'center' }}>
          <IntegrationLogo integrationId="github" size={22} />
        </span>
        <span className="cred-card-title" style={{ fontWeight: 600 }}>GitHub</span>

        <span
          className="conn-type-badge"
          style={{
            background: 'rgba(232, 165, 90, 0.08)',
            color: 'var(--warning)',
            border: '1px solid rgba(232, 165, 90, 0.2)',
          }}
        >
          WRITE (Branches/PRs)
        </span>

        <span className="conn-type-badge conn-type-api-key">GitHub App</span>

        {loading ? (
          <span className="health-badge health-missing">Checking…</span>
        ) : isConnected ? (
          <span className="health-badge health-ok">
            Connected{status.repo_count > 0 ? ` · ${status.repo_count} repos` : ''}
          </span>
        ) : (
          <span className="health-badge health-missing">Not installed</span>
        )}

        <span className="cred-card-chevron">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="cred-card-body">
          {/* Callback feedback */}
          {setupAction === 'install' && !errorCode && (
            <p className="verify-result verify-result--ok" style={{ margin: '8px 0 0' }}>
              GitHub App installed. Your repos are now linked.
            </p>
          )}
          {errorCode === 'repo_access_required' && (
            <p className="verify-result verify-result--error" style={{ margin: '8px 0 0' }}>
              No repositories were selected. Install again and select at least one repo.
            </p>
          )}

          {error && (
            <p className="cred-error" style={{ marginTop: '8px' }}>{error}</p>
          )}

          <div style={{ marginTop: '16px' }}>
            {isConnected ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text)' }}>
                  The SelfHeal GitHub App is installed
                  {status.installation_count > 1
                    ? ` across ${status.installation_count} organizations`
                    : ''}.
                  {status.repo_count > 0
                    ? ` It has access to ${status.repo_count} repositor${status.repo_count === 1 ? 'y' : 'ies'}.`
                    : ' No repositories are currently linked — click Add More Repos.'}
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="cred-save-btn"
                    onClick={() => void handleInstall()}
                    disabled={connecting}
                    style={{ minWidth: '160px' }}
                  >
                    {connecting ? 'Redirecting…' : '+ Add More Repos'}
                  </button>
                  <button
                    type="button"
                    className="verify-btn"
                    onClick={() => void handleDisconnect()}
                    disabled={disconnecting}
                    style={{ color: 'var(--error)', borderColor: 'var(--error)' }}
                  >
                    {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text)', lineHeight: '1.5' }}>
                  Install the SelfHeal GitHub App to let SelfHeal open pull requests with fixes.
                  It uses a limited-access token (no password stored) and supports multiple GitHub organizations.
                </p>
                <div>
                  <button
                    type="button"
                    className="cred-save-btn"
                    onClick={() => void handleInstall()}
                    disabled={connecting}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38
                        0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13
                        -.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87
                        2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95
                        0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82
                        .64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82
                        .44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95
                        .29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013
                        8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
                    </svg>
                    Install GitHub App
                  </button>
                </div>
              </div>
            )}
          </div>

          <p style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
            Each workspace has its own separate installation. Credentials are stored securely in AWS Secrets Manager.
          </p>
        </div>
      )}
    </div>
  )
}
